import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { FaStarOfLife } from 'react-icons/fa';
import Loader from '../components/Loader';
import { getCouponAPI, updateCouponAPI} from '../../services/api/CouponAPI';


const EditCoupon = () => {
    const { id } = useParams();
    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object({
        coupon_code: Yup.string()
          .required('Coupon code is required')
          .max(50, 'Coupon code must be 50 characters or less')
          .matches(/^[A-Za-z0-9]+$/, 'Coupon code must be alphanumeric'),
        coupon_type: Yup.number()
          .required('Coupon type is required')
          .oneOf([1, 2], 'Coupon type must be either 1 (Fixed) or 2 (Percentage)'),
        coupon_value: Yup.number()
          .required('Coupon value is required')
          .min(0, 'Coupon value must be a positive number'),
        expiry_date: Yup.date()
          .required('Expiry date is required')
          .min(new Date(), 'Expiry date must be in the future'),
    });

    const formik = useFormik({
        initialValues: {
            coupon_code: '',
            coupon_type: '',
            coupon_value: '',
            expiry_date: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('coupon_code', values.coupon_code);
            formData.append('coupon_type', values.coupon_type);
            formData.append('coupon_value', values.coupon_value);
            formData.append('expiry_date', values.expiry_date);
            await updateCoupon(formData);
        },
    });

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const data = await getCouponAPI(id, token);
                if (data === null) {
                    navigate('/404');
                } else {
                    formik.setValues({
                        coupon_code: data.coupon_code,
                        coupon_type: data.coupon_type,
                        coupon_value: data.coupon_value,
                        expiry_date: data.expiry_date,
                    });
                }                  
                
            } catch (error) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not fetch coupon data.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        };

        fetchCategory();
    }, [id]);

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        formik.setFieldValue('image', file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue('imagePreview', reader.result);  // Add a preview state if necessary
            };
            reader.readAsDataURL(file);
        }
    };

    const updateCoupon = async (formData) => {
        try {
            const data = await updateCouponAPI(formData, id, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Coupon Updated!',
                    text: 'The coupon has been updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/coupons');
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Edit Coupon">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="coupon_code">Coupon Code <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" id="coupon_code" name="coupon_code" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.coupon_code} />
                                </div>
                                {formik.touched.coupon_code && formik.errors.coupon_code ? ( <label className="text-danger">{formik.errors.coupon_code}</label> ) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-1 required">
                                    <label htmlFor="coupon_type">Coupon Type <FaStarOfLife className='text-danger' /></label>
                                    <div className='d-flex gap-4 my-3'>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="coupon_type" id="fixed" value={1} checked={formik.values.coupon_type === 1} onChange={() => formik.setFieldValue('coupon_type', 1)} />
                                            <label className="form-check-label" htmlFor="fixed">Fixed</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="coupon_type" id="percentage" value={2} checked={formik.values.coupon_type === 2} onChange={() => formik.setFieldValue('coupon_type', 2)} />
                                            <label className="form-check-label" htmlFor="percentage">Percentage</label>
                                        </div>
                                    </div>
                                </div>
                                {formik.touched.coupon_type && formik.errors.coupon_type ? ( <label className="text-danger">{formik.errors.coupon_type}</label>) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="coupon_value">Coupon Value <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" id="coupon_value" name="coupon_value" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.coupon_value} />
                                </div>
                                {formik.touched.coupon_value && formik.errors.coupon_value ? ( <label className="text-danger">{formik.errors.coupon_value}</label> ) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="expiry_date">Expiry Date <FaStarOfLife className='text-danger' /></label>
                                    <input type="date" className="form-control mt-1" id="expiry_date" name="expiry_date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.expiry_date} />
                                </div>
                                {formik.touched.expiry_date && formik.errors.expiry_date ? ( <label className="text-danger">{formik.errors.expiry_date}</label> ) : null}
                            </div>
                        </div>
                        <div className="row mt-3">
                            <div className="col-md-6 col-12 d-flex gap-3">
                                <Link to={'/categories'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default EditCoupon;